import React from 'react';

import classNames from 'classnames';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { validationErrors } from './costAllocationApi';
import DialogModal from './DialogModal';
import { parseNumber } from './formatting';

import Button from 'CommonComponents/Button';

/**
 * Add a column (name, optional note, optional default % for every line) or edit one
 * (name and note). Names are unique within the report, ignoring case.
 */
export default function ColumnModal({ isOpen, onClose, column, columns, onSave }) {
    const editing = !!column;
    const takenNames = columns.filter((other) => other.id !== column?.id).map((other) => other.name.trim().toLowerCase());

    const schema = Yup.object({
        name: Yup.string()
            .trim()
            .required($t('Enter a column name.'))
            .max(191)
            .test('unique', $t('This report already has a column with this name.'), (value) => !takenNames.includes((value || '').trim().toLowerCase())),
        default_pct: Yup.string().test('pct', $t('Enter a percentage between 0 and 999.'), (value) => {
            const pct = parseNumber(value);

            return pct === null || (!Number.isNaN(pct) && pct >= 0 && pct <= 999);
        }),
    });

    const submit = async (values, { setErrors, setSubmitting }) => {
        const payload = { name: values.name.trim(), note: values.note.trim() || null };

        if (!editing) {
            payload.default_pct = parseNumber(values.default_pct);
        }

        try {
            await onSave(payload);
        } catch (error) {
            const errors = validationErrors(error);
            errors && setErrors(errors);
            setSubmitting(false);
        }
    };

    return (
        <DialogModal
            id="cost-allocation-column"
            isOpen={isOpen}
            onClose={onClose}
            title={editing ? $t('Edit column') : $t('Add a column')}
            subtitle={
                editing
                    ? $t('Rename the column or update its note. Allocations already entered are kept.')
                    : $t('A column is one share of the budget, for example a funding partner, a cost type or a venue.')
            }
        >
            <Formik
                initialValues={{ name: column?.name ?? '', note: column?.note ?? '', default_pct: '' }}
                validationSchema={schema}
                validateOnChange={false}
                onSubmit={submit}
            >
                {({ errors, touched, isSubmitting }) => (
                    <Form className="modal__form-container tw-flex tw-flex-col tw-gap-y-4">
                        <div className="tw-flex tw-flex-col tw-gap-y-1.5">
                            <label htmlFor="ca-column-name" className="text-input__label !tw-normal-case">
                                {$t('Column name')}
                            </label>
                            <Field
                                id="ca-column-name"
                                name="name"
                                autoFocus
                                maxLength={191}
                                placeholder={$t('e.g. Regional government')}
                                className={classNames('text-input', { 'tw-border tw-border-red-500': touched.name && errors.name })}
                            />
                            {touched.name && errors.name && <div className="tw-text-sm tw-text-red-500">{errors.name}</div>}
                        </div>
                        <div className="tw-flex tw-flex-col tw-gap-y-1.5">
                            <label htmlFor="ca-column-note" className="text-input__label !tw-normal-case">
                                {$t('Note')}{' '}
                                <span className="tw-font-light tw-normal-case tw-text-charcoal-50">{$t('(optional, shown when hovering the heading)')}</span>
                            </label>
                            <Field
                                as="textarea"
                                id="ca-column-note"
                                name="note"
                                rows={3}
                                placeholder={$t('Basis of the allocation, source agreement, cap or any caveat')}
                                className="text-input"
                            />
                        </div>
                        {!editing && (
                            <div className="tw-flex tw-flex-col tw-gap-y-1.5">
                                <label htmlFor="ca-column-default" className="text-input__label !tw-normal-case">
                                    {$t('Default allocation')}{' '}
                                    <span className="tw-font-light tw-normal-case tw-text-charcoal-50">
                                        {$t('(optional, % applied to every line; adjust individual lines afterwards)')}
                                    </span>
                                </label>
                                <Field
                                    id="ca-column-default"
                                    name="default_pct"
                                    inputMode="decimal"
                                    placeholder={$t('Leave blank to start empty')}
                                    className={classNames('text-input tw-max-w-[220px]', { 'tw-border tw-border-red-500': errors.default_pct })}
                                />
                                {errors.default_pct && <div className="tw-text-sm tw-text-red-500">{errors.default_pct}</div>}
                            </div>
                        )}
                        <div className="modal__footer tw-justify-end tw-gap-x-1.5">
                            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                                {$t('Cancel')}
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSubmitting} loading={isSubmitting}>
                                {editing ? $t('Save changes') : $t('Add column')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </DialogModal>
    );
}
